import React from 'react';
import { Box, Typography, Button, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageHelp, PageHelpProps } from '../PageHelp';

interface Tab {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

interface SectionTabsProps {
  title: string;
  subtitle?: string;
  tabs: Tab[];
  accentColor?: string;
  actionButton?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  help?: PageHelpProps;
}

const SectionTabs: React.FC<SectionTabsProps> = ({
  title,
  subtitle,
  tabs,
  accentColor = '#4a9eff',
  actionButton,
  help,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const activeTabIndex = tabs.findIndex(
    (tab) => location.pathname === tab.path ||
    (tab.path !== tabs[0]?.path && location.pathname.startsWith(tab.path))
  );
  const currentIndex = activeTabIndex >= 0 ? activeTabIndex : 0;

  return (
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 1.5, md: 2 },
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                background: `linear-gradient(135deg, ${accentColor} 0%, #fff 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {title}
            </Typography>
            {help && <PageHelp {...help} />}
          </Box>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actionButton && (
          <Button
            variant="contained"
            onClick={actionButton.onClick}
            startIcon={!isMobile && actionButton.icon}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}dd 100%)`,
              minWidth: { xs: 'auto', sm: 'auto' },
              px: { xs: 1.5, sm: 2 },
              '&:hover': {
                background: `linear-gradient(135deg, ${accentColor}ee 0%, ${accentColor} 100%)`,
              },
            }}
          >
            {isMobile && actionButton.icon ? actionButton.icon : actionButton.label}
          </Button>
        )}
      </Box>

      {/* Tab Bar */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          gap: 0.5,
          p: 0.5,
          borderRadius: { xs: '10px', md: '14px' },
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          // Fade edges on mobile to indicate scrolling
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: { xs: '24px', md: 0 },
            background: 'linear-gradient(to right, transparent, rgba(18, 18, 18, 0.9))',
            pointerEvents: 'none',
            borderRadius: '0 10px 10px 0',
          },
        }}
      >
        {tabs.map((tab, index) => {
          const isActive = index === currentIndex;
          return (
            <Box
              key={tab.path}
              component={motion.div}
              whileHover={{ scale: isActive ? 1 : 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(tab.path)}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, md: 1 },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                py: { xs: 1, md: 1.25 },
                borderRadius: { xs: '8px', md: '10px' },
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
                zIndex: 1,
                color: isActive ? '#fff' : 'text.secondary',
                minHeight: { xs: '44px', md: 'auto' }, // Touch-friendly minimum height
                '&:hover': {
                  color: isActive ? '#fff' : 'text.primary',
                },
              }}
            >
              {/* Active background */}
              {isActive && (
                <Box
                  component={motion.div}
                  layoutId="activeTab"
                  initial={false}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 30,
                  }}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: { xs: '8px', md: '10px' },
                    background: `linear-gradient(135deg, ${accentColor}40 0%, ${accentColor}20 100%)`,
                    border: `1px solid ${accentColor}50`,
                    boxShadow: `0 4px 12px ${accentColor}20`,
                    zIndex: -1,
                  }}
                />
              )}
              {tab.icon && (
                <Box
                  sx={{
                    display: 'flex',
                    color: isActive ? accentColor : 'inherit',
                    '& svg': { fontSize: { xs: 16, md: 18 } },
                  }}
                >
                  {tab.icon}
                </Box>
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isActive ? 600 : 400,
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                  display: { xs: isMobile && tab.icon ? 'none' : 'block', sm: 'block' },
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SectionTabs;
